/**
 * "Gia đình" — the read and write paths of the household itself (plan step 1.8, spec 22.3).
 *
 * Every write here is an RPC: the client has no UPDATE grant on `households`,
 * `household_members` or `financial_subjects`, and that is the point — who belongs to a
 * household decides who can read its ledger, so it is not a table a browser gets to edit.
 *
 * The member list is read from `financial_subjects`, not from `profiles`: RLS lets a person
 * read only their OWN profile row (`profiles_select_own`), while the accounting subject of
 * everyone in the household is visible to all of them. Names on this screen therefore come
 * from the subject, which is also the name every report already uses.
 */
import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

export type FamilyMember = Readonly<{
  userId: string;
  subjectId: string | null;
  displayName: string;
  role: 'owner' | 'member';
  isSelf: boolean;
  joinedAt: string;
}>;

export async function fetchMembers(householdId: string): Promise<readonly FamilyMember[]> {
  const { data: userData } = await supabase.auth.getUser();
  const currentUserId = userData.user?.id ?? null;

  const { data: members, error } = await supabase
    .from('household_members')
    .select('user_id, role, status, joined_at')
    .eq('household_id', householdId)
    .eq('status', 'active')
    .order('joined_at', { ascending: true });
  if (error) throw appErrorException(error);

  const { data: subjects, error: subjectError } = await supabase
    .from('financial_subjects')
    .select('id, profile_id, display_name')
    .eq('household_id', householdId)
    .eq('subject_type', 'person');
  if (subjectError) throw appErrorException(subjectError);

  const byProfile = new Map((subjects ?? []).map((row) => [row.profile_id, row]));

  return (members ?? []).map((member) => {
    const subject = byProfile.get(member.user_id);
    return {
      userId: member.user_id,
      subjectId: subject?.id ?? null,
      displayName: subject?.display_name ?? 'Thành viên',
      role: member.role === 'owner' ? 'owner' : 'member',
      isSelf: member.user_id === currentUserId,
      joinedAt: member.joined_at,
    };
  });
}

/** One-way by design (spec 22.3): a household that has been shared is not un-shared. */
export async function upgradeToFamily(householdId: string, name: string): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('upgrade_to_family', { p_household_id: householdId, p_name: name });
  if (error) throw appErrorException(error);
}

export async function setHouseholdName(householdId: string, name: string): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('set_household_name', { p_household_id: householdId, p_name: name });
  if (error) throw appErrorException(error);
}

/**
 * Adds someone who already has a login (decision B7 — there is no public signup, the owner
 * creates the account in the Supabase dashboard first).
 */
export async function addMemberByEmail(householdId: string, email: string): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('add_member_by_email', { p_household_id: householdId, p_email: email });
  if (error) throw appErrorException(error);
}

export async function setMemberRole(
  householdId: string,
  userId: string,
  role: 'owner' | 'member',
): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('set_member_role', { p_household_id: householdId, p_user_id: userId, p_role: role });
  if (error) throw appErrorException(error);
}

/**
 * Removes a person from the household. Their entries stay posted and their subject is
 * archived — leaving a household does not rewrite the months they were part of (spec 3.3).
 */
export async function removeMember(householdId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('remove_member', { p_household_id: householdId, p_user_id: userId });
  if (error) throw appErrorException(error);
}
